import { useMemo } from 'react';
import styled from 'styled-components';

import { Dimensions } from 'components/insights/constants';
import AnalyticsResponseData from 'components/insights/query/AnalyticsResponseData';
import DataTable from 'components/shared/DataTable';
import SegmentedControl from 'components/shared/SegmentedControl';
import {
  useInboundReferralsInsightsActions,
  useInboundReferralsInsightsStore,
} from 'stores/inboundReferralsInsightsStore';

import {
  getInboundExportParams,
  getInboundMetricExportParams,
  getInboundSlideoutViewByOptions,
  INBOUND_DIMENSION_EXPORT_SLUGS,
  INBOUND_DIMENSION_LABELS,
  INBOUND_METRIC_DRILL_EXPORT_SLUGS,
  INBOUND_METRIC_DRILL_VIEW_BY_OPTIONS,
  slugify,
} from '../constants';
import InboundReferralApiExport from '../InboundReferralApiExport';
import { buildAcceptedColumns, buildAcceptedRows } from '../table/inboundReferralDetailsColumns';
import InboundReferralDetailsTable from '../table/InboundReferralDetailsTable';

type Props = {
  loading: boolean;
  data: AnalyticsResponseData;
  extraFilters: Record<string, unknown> | null;
};

// The slideout's breakdown table: a dynamic View By (the other dimensions) over the same table
// shape as the landing page, scoped to the open slideout's entity. Rows aren't clickable here (no
// nested drill), so it uses the table's default entity cell.
export default function InboundSlideoutTable({ loading, data, extraFilters }: Props) {
  const slideoutContext = useInboundReferralsInsightsStore((state) => state.slideoutContext);
  const slideoutDimension = useInboundReferralsInsightsStore((state) => state.slideoutDimension);
  const { setSlideoutDimension } = useInboundReferralsInsightsActions();

  const viewByOptions = useMemo(() => {
    if (!slideoutContext) return [];
    const dims =
      slideoutContext.kind === 'entity'
        ? getInboundSlideoutViewByOptions(slideoutContext.dimension)
        : INBOUND_METRIC_DRILL_VIEW_BY_OPTIONS;
    return dims.map((d) => ({ label: INBOUND_DIMENSION_LABELS[d] ?? '', value: d }));
  }, [slideoutContext]);

  // The Accepted metric drill uses its own breakdown columns (received / accepted / rate / time);
  // every other case uses the shared determination-rate columns.
  const isAcceptedDrill = slideoutContext?.kind === 'metric' && slideoutContext.metric === 'accepted';

  const exportSlot = useMemo(() => {
    if (!slideoutContext || !slideoutDimension) return null;
    const viewBySlug = INBOUND_DIMENSION_EXPORT_SLUGS[slideoutDimension] ?? '';
    const drillSlug =
      slideoutContext.kind === 'metric'
        ? INBOUND_METRIC_DRILL_EXPORT_SLUGS[slideoutContext.metric]
        : slugify(slideoutContext.entityName);
    const params =
      slideoutContext.kind === 'metric'
        ? getInboundMetricExportParams(slideoutContext.metric, slideoutDimension)
        : getInboundExportParams(slideoutDimension);
    return (
      <InboundReferralApiExport
        params={params}
        extraFilters={extraFilters}
        filename={`inbound-referral-${drillSlug}-by-${viewBySlug}`}
        disabled={loading}
      />
    );
  }, [slideoutContext, slideoutDimension, extraFilters, loading]);

  if (!slideoutDimension) return null;

  const entityHeader = INBOUND_DIMENSION_LABELS[slideoutDimension] ?? '';

  return (
    <Wrapper>
      {(viewByOptions.length > 1 || exportSlot) && (
        <ViewByRow>
          {viewByOptions.length > 1 && (
            <>
              <ViewByLabel>View By:</ViewByLabel>
              <SegmentedControl
                options={viewByOptions}
                selectedValue={slideoutDimension}
                onChange={(val) => setSlideoutDimension(val as Dimensions)}
                mobileFullWidth
              />
            </>
          )}
          {exportSlot && <ExportSlot>{exportSlot}</ExportSlot>}
        </ViewByRow>
      )}
      {isAcceptedDrill ? (
        <DataTable
          columns={buildAcceptedColumns(entityHeader)}
          data={buildAcceptedRows(data)}
          // Default sort is the drill's rate (acceptance rate), matching the chart order.
          defaultSortBy={[{ id: 'acceptanceRate', desc: true }]}
          loading={loading}
          hasFooter={false}
          maxHeight='520px'
          freezeFirstColumn
        />
      ) : (
        <InboundReferralDetailsTable
          loading={loading}
          data={data}
          entityHeader={entityHeader}
          dimension={slideoutDimension}
        />
      )}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  td:first-child:not(:last-child),
  th:first-child {
    border-right: 1px solid ${({ theme }) => theme.colors.black10};
  }

  /* Stretch the divider cell to the full row height so its border spans multi-line rows
     (e.g. a wrapped entity name) instead of stopping at its own content height. */
  td:first-child {
    align-self: stretch;
  }
`;

const ViewByRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 800px) {
    flex-wrap: wrap;
  }
`;

const ExportSlot = styled.div`
  margin-left: auto;
  align-self: flex-start;
  display: flex;
  align-items: center;
`;

const ViewByLabel = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.black75};

  @media (max-width: 800px) {
    display: none;
  }
`;
